/* eslint-disable @ngrx/no-dispatch-in-effects */
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { FinancialInstitutionService } from '@api/api/financialInstitution.service';
import { UserDeviceManagementService } from '@api/api/userDeviceManagement.service';
import { UpdateDeviceResponse } from '@api/index';
import { AttachDeviceResponse } from '@api/model/attachDeviceResponse';
import { NOTICE_TYPE } from '@core/constants';
import { ERROR_TYPE, OIDC_CODE } from '@core/constants/error-code';
import { PBBEvents } from '@core/constants/events';
import { PBBHTTPHeaders } from '@core/constants/http-headers';
import { fullScreenModalGreyConfig } from '@core/constants/modal.config';
import { REDIRECT_QUERY_PARAMS_KEYS } from '@core/constants/query-params';
import { SNAME } from '@core/constants/storage';
import { Languages } from '@core/services/language.service';
import { LoggerService } from '@core/services/logger.service';
import { NoticeService } from '@core/services/notice.service';
import { WindowService } from '@core/services/window.service';
import * as Utils from '@core/utils';
import { ErrorPageComponent } from '@error/error-page/error-page.component';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as CheckoutActions from '@store/checkout/checkout.actions';
import { catchError, exhaustMap, filter, from, map, of, startWith, switchMap, tap, withLatestFrom } from 'rxjs';
import { v4 } from 'uuid';
import * as CoreActions from './core.actions';
import { selectDeviceId, selectFinancialInstitution } from './core.selectors';
import {selectUserProfileEmail} from '@store/user/user.selectors';
import {Notices} from './core.interface';

@Injectable()
export class CoreEffects {
  private toBase64Url(buffer: Uint8Array): string {
    let binary = '';
    for (const byte of buffer) {
      binary += String.fromCharCode(byte);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  }

  private generateCodeVerifier(length = 64): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    const randomValues = new Uint8Array(length);
    if (this.window.crypto?.getRandomValues) {
      this.window.crypto.getRandomValues(randomValues);
    } else {
      for (let i = 0; i < randomValues.length; i++) {
        randomValues[i] = Math.floor(Math.random() * 256);
      }
    }

    let verifier = '';
    for (let i = 0; i < randomValues.length; i++) {
      verifier += charset[randomValues[i] % charset.length];
    }
    return verifier;
  }

  private async createPkceChallenge(codeVerifier: string): Promise<string> {
    if (!this.window.crypto?.subtle) {
      return codeVerifier;
    }
    const data = new TextEncoder().encode(codeVerifier);
    const digest = await this.window.crypto.subtle.digest('SHA-256', data);
    return this.toBase64Url(new Uint8Array(digest));
  }

  private normalizeRedirectUri(redirectUrl: string): string {
    if (this.window.location.origin.indexOf('localhost') !== -1) {
      return new URL(redirectUrl || '', this.window.location.origin).toString();
    }
    return `${redirectUrl || this.window.location.pathname}`;
  }

  private buildPkceRedirectData(payload: {
    redirectUrl?: string;
    consentId?: string;
    email?: string;
    stepUp?: boolean;
    fi_user_id?: string;
    login_hint?: string;
    nonce?: string;
    userProfileRef?: string;
    rus?: boolean;
    selectedFI?: { oauth_url?: string; id?: string };
  }): Promise<{ authURL: string; stepUp?: boolean; selectedFIId?: string; rus?: boolean }> {
    return (async () => {
      let authURL = `${payload?.selectedFI?.oauth_url}`;
      const correlationId = sessionStorage.getItem(SNAME.CORRELATION_ID) || v4();
      const requestId = v4();
      const redirectUri = this.normalizeRedirectUri(payload.redirectUrl || '');
      const redirectUrlParam = (() => {
        // Local FI flows require a full callback URL for redirect_url.
        if (this.window.location.origin.indexOf('localhost') !== -1) {
          return redirectUri;
        }

        try {
          const parsed = new URL(redirectUri);
          return `${parsed.pathname}${parsed.search}`;
        } catch {
          return redirectUri;
        }
      })();
      const codeVerifier = this.generateCodeVerifier();
      const codeChallenge = await this.createPkceChallenge(codeVerifier);

      sessionStorage.setItem(SNAME.CORRELATION_ID, correlationId);
      sessionStorage.setItem(SNAME.PKCE_CODE_VERIFIER, codeVerifier);

      let authParams = new HttpParams({})
        .append(PBBHTTPHeaders.correlationId, correlationId)
        .append(PBBHTTPHeaders.requestId, requestId)
        .append(REDIRECT_QUERY_PARAMS_KEYS.redirectUrl, redirectUrlParam)
        .append(REDIRECT_QUERY_PARAMS_KEYS.channelIndicator, 'ONLINE')
        .append(REDIRECT_QUERY_PARAMS_KEYS.codeChallenge, codeChallenge)
        .append(REDIRECT_QUERY_PARAMS_KEYS.codeChallengeMethod, 'S256');

      if (payload.consentId) {
        authParams = authParams.append(REDIRECT_QUERY_PARAMS_KEYS.consentId, payload.consentId);
      }
      if (payload.email) {
        authParams = authParams.append(REDIRECT_QUERY_PARAMS_KEYS.email, encodeURIComponent(payload.email) || '');
      }
      if (payload.stepUp && payload.fi_user_id) {
        authParams = authParams.append(REDIRECT_QUERY_PARAMS_KEYS.stepupRequired, true);
        authParams = authParams.append(REDIRECT_QUERY_PARAMS_KEYS.fiUserId, payload.fi_user_id);
      }
      if (payload.login_hint) {
        authParams = authParams.append(REDIRECT_QUERY_PARAMS_KEYS.loginHint, payload.login_hint);
      }
      if (payload.nonce) {
        authParams = authParams.append(REDIRECT_QUERY_PARAMS_KEYS.nonce, payload.nonce);
      }
      if (payload.userProfileRef) {
        authParams = authParams.append(REDIRECT_QUERY_PARAMS_KEYS.userProfileRef, payload.userProfileRef);
      }
      if (payload.rus) {
        authParams = authParams.append(REDIRECT_QUERY_PARAMS_KEYS.rus, true);
      }
      authParams = authParams.append(
        REDIRECT_QUERY_PARAMS_KEYS.locale,
        this.translateService.getCurrentLang() || Languages.ENGLISH
      );
      authURL = `${authURL}?${authParams.toString()}`;

      return {
        authURL,
        stepUp: payload.stepUp,
        selectedFIId: payload.selectedFI?.id,
        rus: payload.rus,
      };
    })();
  }

  getFinancialInstitution$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CoreActions.getFinancialInsitutions),
      exhaustMap(() =>
        this.financialInstituteService.getFIAll().pipe(
          map((res) =>
            CoreActions.getFinancialInsitutionsSuccess({
              financialInstitutions: Utils.sortAlphabetic(res, 'display_name'),
            })
          ),
          catchError((error) => {
            return of(CoreActions.getFinancialInsitutionsFailed({ error }));
          })
        )
      ),
      startWith(
        CoreActions.hydrateCore({
          tempemail: sessionStorage.getItem(SNAME.TEMP_EMAIL) || '',
          OIDCCode: (sessionStorage.getItem(SNAME.OIDCCODE) || '') as OIDC_CODE,
        })
      )
    );
  });

  fatalEffect$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(...[CoreActions.getFinancialInsitutionsFailed, CheckoutActions.getConsentFailed]),
        tap((action) => {
          if (action.error instanceof HttpErrorResponse && action.error.status === 401) {
            if (this.merchantWindowService.consentPayload) {
              this.store.dispatch(
                CheckoutActions.getConsentSuccess({ consent: this.merchantWindowService.consentPayload })
              );
            } else {
              this.store.dispatch(CheckoutActions.getConsentStart());
              this.merchantWindowService.sendToMerchant(PBBEvents.konekLoaded);
            }
            this.logger.log('AUTH', 'TOKEN EXPIRED');
          } else {
            this.merchantWindowService.sendToMerchant(PBBEvents.konekError, true, action.type);
          }
        })
      );
    },
    {
      dispatch: false,
    }
  );

  errorEffect$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CheckoutActions.postConsentGrantFailed),
        tap(() => {
          this.dialog.closeAll(); // closing payment in progress dialog
          this.merchantWindowService.sendToMerchant(PBBEvents.konekWarning, false);
        })
      );
    },
    {
      dispatch: false,
    }
  );

  pbbSetErrorEffect$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(...[CoreActions.setError]),
        tap((action) => {
          if (action.errorType === ERROR_TYPE.OVERLAY) {
            this.dialog.open(ErrorPageComponent, {
              data: { errorCode: action.errorCode },
              ...fullScreenModalGreyConfig,
            });
            return;
          }
          const baseUrl = Utils.isCheckout() ? 'checkout/' : 'user-portal/';
          return this.router.navigate(
            [`${action.errorBaseUrl === undefined ? baseUrl : action.errorBaseUrl}error/${action.errorCode}`],
            { queryParams: { retryUrl: action.retryUrl } }
          );
        })
      );
    },
    {
      dispatch: false,
    }
  );

  setMerchantReferrer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CoreActions.setMerchantReferrer),
      tap((action) => sessionStorage.setItem(SNAME.MERCHANT, action.merchantReferrer)),
      map((action) =>
        CoreActions.setFooterConfig({
          footerConfig: {
            hasMerchantOpener: !!action.merchantReferrer,
          },
        })
      )
    );
  });

  // SELECT FI
  selectFI$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CoreActions.selectFI),
        filter((payload) => (payload.noredirect ? false : true)),
        switchMap((payload) => from(this.buildPkceRedirectData(payload))),
        map(({ authURL, stepUp, selectedFIId, rus }) => {
          if (rus) {
            this.store.dispatch(
              CoreActions.redirectTOFILogin({
                authURL,
              })
            );
            return authURL;
          }
          if (stepUp) {
            const url = `${location.pathname}/fi-redirect`;
            this.router.navigate([url], {
              queryParams: {
                authURL,
                stepUp,
                selectedFIId,
              },
            });
          } else {
            this.store.dispatch(
              CoreActions.redirectTOFILogin({
                authURL,
              })
            );
          }

          return authURL;
        })
      );
    },
    { dispatch: false }
  );

  selectFIByID$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CoreActions.selectFIByID),
      switchMap((action) =>
        this.store.select(selectFinancialInstitution(action.selectedFIId)).pipe(
          map((selectedFI) =>
            CoreActions.selectFI({
              selectedFI,
              noredirect: true,
              stepUp: false,
            })
          )
        )
      )
    );
  });

  redirectToFI$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CoreActions.redirectTOFILogin),
        map(({ authURL }) => {
          this.logger.log('AUTH', 'REDIRECTING TO FI ', authURL);
          if (Utils.isCheckout()) {
            this.merchantWindowService.handleHeartBeat(-1);
          }
          this.window.location.replace(authURL);
        })
      );
    },
    {
      dispatch: false,
    }
  );

  setOIDCCode$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CoreActions.setOIDCCode),
        tap((action) => {
          sessionStorage.setItem(SNAME.OIDCCODE, action.OIDCCode || '');

          if (action.OIDCCode === '4105') {
            this.logger.log('AUTH', 'USER NOT FOUND', 'LOCAL STORAGE CLEARED');
            localStorage.clear();
          }
        })
      );
    },
    {
      dispatch: false,
    }
  );

  setTempEmail$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CoreActions.setTempEmail),
        tap((action) => {
          sessionStorage.setItem(SNAME.TEMP_EMAIL, action.tempemail);
        })
      );
    },
    {
      dispatch: false,
    }
  );

  // device management
  attachDevice$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CoreActions.attachDevice),
      switchMap((action) => {
        return this.userDeviceManagementService
          .attachDevice(action.userRef, {
            user_agent: this.window.navigator.userAgent,
          })
          .pipe(
            map((resp: AttachDeviceResponse) => {
              if (resp.device_ref) {
                return CoreActions.attachDeviceSuccess({ deviceId: resp.device_ref, userRef: action.userRef });
              } else {
                return CoreActions.attachDeviceFailed({ error: new Error('No device_ref') });
              }
            }),
            catchError((error) => of(CoreActions.attachDeviceFailed({ error })))
          );
      })
    );
  });

  attachDeviceSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CoreActions.attachDeviceSuccess),
        map(({ deviceId, userRef }) => {
          this.logger.log('AUTH', 'DEVICE REF ADDED');
          sessionStorage.setItem(Utils.getSessionDeviceIdKeyName(userRef), deviceId);
          Utils.injectProxyHeader(deviceId);
          this.logger.log('WEBAUTHN', 'TRANSMIT PROXY SET');
        })
      );
    },
    { dispatch: false }
  );

  // device update
  updateDevice$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CoreActions.updateDevice),
      switchMap((action) => {
        return this.userDeviceManagementService
          .updateDevices(action.userRef, action.deviceRef, action.deviceDetails)
          .pipe(
            map((res: UpdateDeviceResponse) => {
              return CoreActions.updateDeviceSuccess({ userJWS: res.user_jws, deviceRef: action.deviceRef });
            }),
            catchError((error) => of(CoreActions.updateDeviceFailed({ error })))
          );
      })
    );
  });

  updateDeviceSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CoreActions.updateDeviceSuccess),
        concatLatestFrom(() => this.store.select(selectDeviceId)),
        tap(([action, deviceRef]) => {
          if (action.deviceRef === deviceRef) {
            localStorage.setItem(SNAME.USER_JWS, action.userJWS);
          }
        })
      );
    },
    {
      dispatch: false,
    }
  );

  showSnackBar$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CoreActions.showNotice),
        concatLatestFrom(() => this.store.select(selectUserProfileEmail)),
        map(([{ notice, error }, email]) => {
          if (notice) {
            this.ns.clearNotices();
            if (notice === Notices.USER_REGISTRATION && !error) {
              // User registration success banner
              this.ns.addBannerNotice({
              noticeVariant: 'success',
              noticeTitle: this.translateService.instant('USER_REGISTRATION_SUCCESS.TITLE'),
              noticeMessage: this.translateService.instant('USER_REGISTRATION_SUCCESS.SUBTITLE').replace('{{email}}', email || ''),
              dismissable: true,
              duration: 0,
              displayNoticeIcon: true,
              showCloseButton: true
              });
              return;
            }

            const isStepupRetryError = notice === 'STEPUP_RETRY' && error;
            let title: string;
            if (isStepupRetryError) {
              title = 'COMMON.STEPUP_RETRY_ERROR_TITLE';
            } else {
              title = `COMMON.${notice}_${error ? 'ERROR' : 'SUCCESS'}`;
            }
            this.ns.addNotice({
              type: error ? NOTICE_TYPE.ERROR : NOTICE_TYPE.INFO,
              title: title,
              subtitle: isStepupRetryError ? 'COMMON.STEPUP_RETRY_ERROR' : undefined,
              dismissable: true,
              // duration: this.duration,
            });
          }
        })
      );
    },
    {
      dispatch: false,
    }
  );
  constructor(
    private actions$: Actions,
    private readonly store: Store,
    private financialInstituteService: FinancialInstitutionService,
    private readonly router: Router,
    public merchantWindowService: WindowService,
    public dialog: MatDialog,
    public window: Window,
    private readonly logger: LoggerService,
    private userDeviceManagementService: UserDeviceManagementService,
    private readonly translateService: TranslateService,
    private ns: NoticeService
  ) {}
}
